import type { ChangeEvent } from "react";
import TendencyOverrideControl from "./TendencyOverrideControl.tsx";
import {
	BASKETBALL_PLAYER_TENDENCY_FIELDS,
	SHARED_PLAYER_TENDENCY_FIELDS,
	getFootballPlayerTendencyFields,
} from "../../../common/playerTendencyOverrides.ts";
type PlayerTendencyOverrides = Record<string, number> & {
	locked?: Record<string, boolean>;
};

type TendencyOverridesFormProps = {
	godMode: boolean;
	pos: string;
	tendencyOverrides?: PlayerTendencyOverrides;
	handleChange: (
		type: string,
		field: string,
		event:
			| ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
			| {
					target: {
						value: any;
						checked?: boolean;
					};
			  },
	) => void;
};

const TendencyOverridesForm = ({
	godMode,
	pos,
	tendencyOverrides,
	handleChange,
}: TendencyOverridesFormProps) => {
	if (!godMode) {
		return null;
	}

	const overrideFields =
		__SPORT === "football"
			? [
					...getFootballPlayerTendencyFields(pos),
					...SHARED_PLAYER_TENDENCY_FIELDS,
				]
			: __SPORT === "basketball"
				? [
						...BASKETBALL_PLAYER_TENDENCY_FIELDS,
						...SHARED_PLAYER_TENDENCY_FIELDS,
					]
				: SHARED_PLAYER_TENDENCY_FIELDS;

	const handleTendencyChange = (field: string, value: number | undefined) => {
		handleChange("tendencyOverrides", field, {
			target: { value },
		});
	};

	const handleLockChange = (field: string, locked: boolean) => {
		handleChange("tendencyOverrides", `locked.${field}`, {
			target: { value: locked, checked: locked },
		} as any);
	};

	const resetAllTendencies = () => {
		overrideFields.forEach((field) => {
			handleChange("tendencyOverrides", field.key, {
				target: { value: undefined },
			});
		});
	};

	const hasAnyOverrides = overrideFields.some(
		(field) =>
			tendencyOverrides?.[field.key as keyof PlayerTendencyOverrides] !==
				undefined &&
			tendencyOverrides?.[field.key as keyof PlayerTendencyOverrides] !== 0,
	);

	// Check if any values exceed 100 (high multiplier warning)
	const hasHighValues = overrideFields.some((field) => {
		const value = tendencyOverrides?.[
			field.key as keyof PlayerTendencyOverrides
		] as number | undefined;
		return value !== undefined && value > 100;
	});

	return (
		<div className="tendency-overrides-form">
			<div className="row">
				{overrideFields.map((field) => (
					<div key={field.key} className="col-md-6 mb-3">
						<TendencyOverrideControl
							label={field.label}
							description={field.description}
							fieldKey={field.key}
							value={
								tendencyOverrides?.[
									field.key as keyof PlayerTendencyOverrides
								] as number | undefined
							}
							locked={
								tendencyOverrides?.locked?.[
									field.key as keyof PlayerTendencyOverrides["locked"]
								] as boolean | undefined
							}
							disabled={!godMode}
							onChange={handleTendencyChange}
							onLockChange={handleLockChange}
						/>
					</div>
				))}
			</div>

			{hasAnyOverrides && (
				<div className="row mt-3">
					<div className="col-12">
						<div className="alert alert-info">
							<h6 className="alert-heading">
								<i className="glyphicon glyphicon-info-sign me-2" />
								Tendency Override Information
							</h6>
							<p className="mb-2">
								Tendency overrides will reset to zero at the start of each
								season unless locked. Locked overrides will persist across
								seasons.
							</p>
							<small className="text-muted">
								Values range from -100 to unlimited positive. Negative values
								decrease the tendency, positive values increase it, and zero
								uses the calculated tendency.
							</small>
						</div>
					</div>
				</div>
			)}

			{hasHighValues && (
				<div className="row mt-2">
					<div className="col-12">
						<div className="alert alert-warning">
							<h6 className="alert-heading">
								<i className="glyphicon glyphicon-warning-sign me-2" />
								⚠️ High Value Warning
							</h6>
							<p className="mb-2">
								Some tendency overrides have values over 100, which will create
								extreme multipliers and potentially unrealistic game effects.
							</p>
							<p className="mb-0">
								<strong>Examples:</strong> 100 = 100.0x effect, 500 = 500.0x
								effect, 1000 = 1000.0x effect
							</p>
						</div>
					</div>
				</div>
			)}

			<div className="row mt-3">
				<div className="col-12">
					<button
						type="button"
						className="btn btn-secondary btn-sm"
						onClick={resetAllTendencies}
						disabled={!godMode || !hasAnyOverrides}
					>
						<i className="glyphicon glyphicon-refresh me-1" />
						Reset All Tendencies
					</button>
				</div>
			</div>
		</div>
	);
};

export default TendencyOverridesForm;
